import React, { useEffect, useState } from "react";
import { TwilioAudioCallEvent, firebaseCollections, CallStatus } from "globals/enums";
import { alertHelper } from "helpers/alert-helper";
import { AlertName } from "helpers/alert-type";
import { AlertLogConfig } from "services/session-token";
import phoneicon from "assets/img/phoneicon.png";
import chatrequest from "assets/img/chatrequest.png";
import connector from "assets/img/newloader.gif";
import voliume from "assets/img/voliume.png";
import chatClse from "assets/img/chatClse.png";
import { handleError } from "helpers/sentry";
import { useAppDispatch, useAppSelector } from "store/store";
import { setTwilioAudioCallStatus } from "store/app.slice";

import { getDatabase, ref, onValue, update } from "firebase/database";
import { getSystemTime } from "helpers/common";

const MessageAudioBox: React.FC = () => {
  const dispatch = useAppDispatch();
  const [audioCallStatus, setAudioCallStatus] = useState<string>("");
  const alertConfig: AlertLogConfig[] = useAppSelector((state) => state.app.alertConfigs);
  const participantGroupId = useAppSelector((state) => state.app.channelData.participant_group_id);
  const twilioCallStatus = useAppSelector((state) => state.app.twilioAudioCallStatus);

  const { attendee_id: attendeeId, session_id: sessionId } =
    useAppSelector((state) => state.app?.session) || {};

  const getAudioCallRef = () =>
    ref(getDatabase(), `${firebaseCollections.AudioCall}/${sessionId}/`);

  const handleAudioCallStatus = async () => {
    try {
      await update(getAudioCallRef(), {
        status: CallStatus.Requested,
        participant_group_id: participantGroupId,
        attendee_id: attendeeId,
        requested_timestamp: getSystemTime().toISOString()
      });
    } catch (error) {
      handleError(error);
    }
  };

  const handleCallRequest = async () => {
    if (navigator.onLine) {
      await handleAudioCallStatus();
      try {
        alertHelper(alertConfig).raiseAlert(AlertName.AudioCallRequested, dispatch);
        dispatch(
          setTwilioAudioCallStatus({ twilio_audio_call_status: TwilioAudioCallEvent.Requested })
        );
      } catch (error) {
        handleError(error);
      }
    }
  };

  useEffect(() => {
    if (!sessionId) return;
    try {
      const unsubscribe = onValue(getAudioCallRef(), (audioCallData) => {
        const status = audioCallData.val()?.status ?? CallStatus.Stale;
        setAudioCallStatus(status);
      });
      return () => unsubscribe();
    } catch (error) {
      handleError(error);
    }
  }, [sessionId]);

  const updateCallStatus = async (audioCallStatus: any) => {
    const audioCallData = {
      participant_group_id: participantGroupId,
      attendee_id: attendeeId
    };

    try {
      await update(getAudioCallRef(), { ...audioCallData, ...audioCallStatus });
    } catch (error) {
      handleError(error);
    }
  };

  useEffect(() => {
    switch (twilioCallStatus) {
      case TwilioAudioCallEvent.Requested:
        updateCallStatus({
          status: CallStatus.Requested,
          requested_timestamp: getSystemTime().toISOString()
        });
        break;
      case TwilioAudioCallEvent.Accept:
        updateCallStatus({
          status: CallStatus.Connected,
          connected_timestamp: getSystemTime().toISOString()
        });
        break;
      case TwilioAudioCallEvent.Disconnect:
      case TwilioAudioCallEvent.Cancel:
      case TwilioAudioCallEvent.Ignore:
        updateCallStatus({
          status: CallStatus.Stale,
          disconnected_timestamp: getSystemTime().toISOString()
        });
        break;
      default:
        break;
    }
  }, [twilioCallStatus]);

  // Twilio's accept event is the authoritative signal that a call is live.
  // Firebase status can be overwritten by the proctor's dashboard at any time,
  // so we must not let a Firebase "stale" write hide an actively connected call.
  const isLocalCallActive = twilioCallStatus === TwilioAudioCallEvent.Accept;

  return (
    <div className="tvp-audio-chat-wrapper" aria-live="polite">
      {(audioCallStatus === CallStatus.Stale || audioCallStatus === CallStatus.Disconnected) &&
        !isLocalCallActive && (
          <button
            className={`tvp-callBtn ${navigator.onLine ? "" : "tvp-disabled"}`}
            onClick={handleCallRequest}
            autoFocus={true}
          >
            <img src={phoneicon} alt="Audio call Icon" title="Make Audio call" />
          </button>
        )}
      {(audioCallStatus === CallStatus.Initiated || audioCallStatus === CallStatus.Requested) &&
        !isLocalCallActive && (
          <div className="tvp-requested">
            <span className="tvp-chatStatus1">Call Requested</span>
            <img
              className="tvp-connecting"
              src={chatrequest}
              alt="Audio call connecting"
              title="Audio call connecting"
            />
            <img className="tvp-connecting" src={connector} alt="Icon" />
          </div>
        )}
      {(audioCallStatus === CallStatus.Connected || isLocalCallActive) && (
        <div className="tvp-requested">
          <img className="tvp-connecting" src={voliume} alt="Icon" />
          <span className="tvp-chatStatus2"> Ongoing</span>
          <span className="tvp-chatStatus2"></span>

          <img className="tvp-connecting" src={chatClse} alt="Icon" />
          <div className="tvp-chatClse">
            <img className="tvp-connecting" src={chatClse} alt="Icon" />
          </div>
        </div>
      )}
    </div>
  );
};

export default MessageAudioBox;
